import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { KanbanSquare, List, PackageX, Plus, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import {
  Button,
  Chip,
  DataTable,
  EmptyState,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  Select,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  ViewToggle,
} from '@/components/ui'
import { useHasAnyPermission } from '@/shell/module-system'
import { useCaseList, useCaseStats, useTransitionCase } from '../api/queries'
import type { CaseSummary, Stage } from '../api/types'
import {
  BOARD_STAGES,
  CATEGORY_LABELS,
  CLAIM_FILED_WITH_TYPE_LABELS,
  STAGE_LABELS,
  STAGE_VARIANT,
  filedWithLabel,
} from '../utils/stage-meta'
import { CaseCreateDrawer } from '../components/CaseCreateDrawer'
import { CaseDetailDrawer } from '../components/CaseDetailDrawer'
import { MaterialDispositionKanban } from '../components/MaterialDispositionKanban'

type ViewMode = 'table' | 'kanban'

const VIEW_OPTIONS = [
  { value: 'table' as const, label: 'Table', icon: List },
  { value: 'kanban' as const, label: 'Kanban', icon: KanbanSquare },
]

type StageFilter = 'all' | Stage

const STAGE_FILTER_OPTIONS: { value: StageFilter; label: string }[] = [
  { value: 'all', label: 'All stages' },
  ...BOARD_STAGES.map((s) => ({ value: s, label: STAGE_LABELS[s] })),
]

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatMoney(value: string | null | undefined): string {
  if (!value) return '$0'
  const n = Number(value)
  if (Number.isNaN(n)) return '$0'
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

export function MaterialDispositionPage() {
  const [view, setView] = useState<ViewMode>('kanban')
  const [stageFilter, setStageFilter] = useState<StageFilter>('all')
  const [createOpen, setCreateOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Finance is view-only for this board — write endpoints 403 server-
  // side; hiding the affordances here just avoids a round-trip failure.
  const canEdit = useHasAnyPermission(['admin', 'operations', 'purchasing'])

  const stats = useCaseStats()
  const list = useCaseList(stageFilter === 'all' ? {} : { stage: stageFilter })
  const transition = useTransitionCase()

  const items = list.data?.items ?? []

  const counts = stats.data?.counts_by_stage ?? {}
  const openCount =
    (counts.open ?? 0) +
    (counts.investigating ?? 0) +
    (counts.resolution_pending ?? 0) +
    (counts.claim_pending ?? 0)
  const total = stats.data?.total ?? 0
  const oldestAge = stats.data?.oldest_open_age_days

  const columns = useCaseColumns()

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-canvas">
      <PageHeaderBand
        title="Material Disposition"
        description="Damaged, lost, quarantined, or otherwise non-sellable material — tracked from discovery through write-off, vendor claim, return, or release."
      >
        <div className="ml-auto">
          <MetricStrip variant="inline">
            <MetricCard variant="inline" label="Open cases" value={String(openCount)} />
            <MetricCard
              variant="inline"
              label="$ exposure outstanding"
              value={formatMoney(stats.data?.open_exposure_value)}
            />
            <MetricCard
              variant="inline"
              label="$ written off"
              value={formatMoney(stats.data?.written_off_value)}
            />
            <MetricCard
              variant="inline"
              label="Oldest open"
              value={oldestAge != null ? `${oldestAge}d` : '—'}
            />
            <MetricCard variant="inline" label="Total" value={String(total)} />
          </MetricStrip>
        </div>
      </PageHeaderBand>

      <Toolbar>
        <ToolbarLeft>
          <Select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value as StageFilter)}
            aria-label="Filter by stage"
          >
            {STAGE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => list.refetch()}
            disabled={list.isFetching}
          >
            <RefreshCw size={14} className="mr-1.5" />
            Refresh
          </Button>
        </ToolbarLeft>
        <ToolbarRight>
          <ViewToggle<ViewMode>
            options={VIEW_OPTIONS}
            value={view}
            onChange={setView}
            aria-label="View mode"
          />
          {canEdit ? (
            <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
              <Plus size={14} className="mr-1.5" />
              New case
            </Button>
          ) : null}
        </ToolbarRight>
      </Toolbar>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
        {list.isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="m-0 text-sm text-tertiary">Loading…</p>
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={PackageX}
            title="No cases here yet"
            description={
              stageFilter === 'all'
                ? 'Open a case when damaged, lost, quarantined, or off-spec material is found — CARLA has no autonomous entry point for this yet, so start it here.'
                : `No cases match the "${STAGE_FILTER_OPTIONS.find((o) => o.value === stageFilter)?.label ?? stageFilter}" filter.`
            }
            action={
              stageFilter === 'all' && canEdit ? (
                <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
                  <Plus size={14} className="mr-1.5" />
                  New case
                </Button>
              ) : null
            }
          />
        ) : view === 'kanban' ? (
          <MaterialDispositionKanban
            items={items}
            selectedId={selectedId ?? undefined}
            onSelect={(c) => setSelectedId(c.id)}
            onTransition={(c, target) => {
              if (!canEdit) return
              transition.mutate(
                { caseId: c.id, targetStage: target },
                {
                  onError: (err) => {
                    const msg = err instanceof Error ? err.message : 'transition failed'
                    toast.error(msg, { duration: 5000 })
                  },
                },
              )
            }}
          />
        ) : (
          <DataTable
            data={items}
            columns={columns}
            loading={list.isLoading}
            getRowId={(r) => r.id}
            onRowClick={(r) => setSelectedId(r.id)}
            moduleId="material-disposition"
            maxHeight="100%"
            columnWidths={{
              case_number: 140,
              product: 220,
              category: 160,
              value: 120,
              stage: 140,
              filed_with: 170,
              created_at: 110,
            }}
            minTableWidth={1180}
            emptyState={
              <EmptyState variant="no-match" title="No matches" description="Try clearing the stage filter." />
            }
          />
        )}
      </div>

      {canEdit ? (
        <CaseCreateDrawer
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={(caseId) => setSelectedId(caseId)}
        />
      ) : null}
      {/* Keyed by case so switching rows with the drawer still open
          remounts it — otherwise the Claim section's expanded state and
          any un-blurred field drafts would carry over to the next case. */}
      <CaseDetailDrawer
        key={selectedId ?? 'none'}
        caseId={selectedId}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null)
        }}
      />
    </div>
  )
}

function useCaseColumns(): ColumnDef<CaseSummary, unknown>[] {
  return useMemo<ColumnDef<CaseSummary, unknown>[]>(
    () => [
      {
        id: 'case_number',
        header: 'Case #',
        accessorFn: (r) => r.case_number,
        cell: (ctx) => <span className="text-secondary">{ctx.row.original.case_number}</span>,
      },
      {
        id: 'product',
        header: 'Product',
        accessorFn: (r) => r.first_product_name ?? '',
        cell: (ctx) => {
          const r = ctx.row.original
          if (!r.first_product_name) return <em className="text-tertiary">unknown</em>
          return r.line_count > 1 ? `${r.first_product_name} +${r.line_count - 1} more` : r.first_product_name
        },
      },
      {
        id: 'category',
        header: 'Category',
        accessorFn: (r) => CATEGORY_LABELS[r.category],
        cell: (ctx) => (
          <span className="text-secondary">{CATEGORY_LABELS[ctx.row.original.category]}</span>
        ),
      },
      {
        id: 'value',
        header: 'Est. value',
        accessorFn: (r) => Number(r.estimated_value ?? 0),
        meta: { numeric: true },
        cell: (ctx) => (
          <span className="text-secondary">{formatMoney(ctx.row.original.estimated_value)}</span>
        ),
      },
      {
        id: 'stage',
        header: 'Stage',
        accessorFn: (r) => STAGE_LABELS[r.stage],
        cell: (ctx) => (
          <Chip size="xs" variant={STAGE_VARIANT[ctx.row.original.stage]} dot>
            {STAGE_LABELS[ctx.row.original.stage]}
          </Chip>
        ),
      },
      {
        id: 'filed_with',
        header: 'Filed with',
        // Sort on the display string so unnamed claims still group by type
        // instead of scattering among the blanks.
        accessorFn: (r) => filedWithLabel(r) ?? '',
        cell: (ctx) => {
          const r = ctx.row.original
          const name = filedWithLabel(r)
          if (!name) return <em className="text-tertiary">—</em>
          const typeLabel = r.claim_filed_with_type
            ? CLAIM_FILED_WITH_TYPE_LABELS[r.claim_filed_with_type]
            : null
          return (
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-secondary">{name}</span>
              {typeLabel && typeLabel !== name ? (
                <span className="text-role-metadata">{typeLabel}</span>
              ) : null}
            </div>
          )
        },
      },
      {
        id: 'created_at',
        header: 'Created',
        accessorKey: 'created_at',
        cell: (ctx) => (
          <span className="text-tertiary">{formatDate(ctx.row.original.created_at)}</span>
        ),
      },
    ],
    [],
  )
}
